import { create, type StateCreator } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AuthStore } from './auth.types';
import { AuthRepositoryImpl } from '../../infrastructure/repositories/auth.repository.impl';
import { ApiRestDataSource } from '../../infrastructure/datasources/api.rest.datasource';
import { FirebaseAuthDataSource } from '../../infrastructure/datasources/firebase.auth.datasource';
import { MsalAuthDataSource } from '../../infrastructure/datasources/msal.auth.datasource';
import { createJSONStorage } from 'zustand/middleware';
import Cookies from 'js-cookie';
import {
  type IChangePassword,
  type IForgotPassword,
  type IUser,
  type IUserLogin,
  type IUserRegister,
  type IValidateAccount,
} from '../../domain/interfaces/auth.interface';
import { ESignInMethod } from '../../domain/enums/auth.enum';
import { logger } from '@/core/utils/logger';
import { clearBrowserStorage } from '@/core/utils/clear-browser-storage';
import { IpService } from '@/core/services/ip.service';
import { toIso3, DEFAULT_SIGNER_COUNTRY_ISO3 } from '@/data/phone-countries';

// Crear instancias de los datasources
const apiRestDataSource = new ApiRestDataSource();
const firebaseAuthDataSource = new FirebaseAuthDataSource();
const msalAuthDataSource = new MsalAuthDataSource();

// Crear una instancia del repositorio
const authRepository = new AuthRepositoryImpl(apiRestDataSource);

const cookieStorage = {
  getItem: (name: string): string | null => {
    const value = Cookies.get(name);
    return value ? value : null;
  },
  setItem: (name: string, value: string): void => {
    // Establecer la cookie principal
    Cookies.set(name, value, {
      expires: 7, // Expira en 7 días
      path: '/',
      sameSite: 'lax',
    });

    // Si es la cookie de autenticación, también establecer el token separado
    if (name === 'auth-storage') {
      try {
        const authData = JSON.parse(value);
        if (authData.state?.token) {
          Cookies.set('auth_token', authData.state.token, {
            expires: 7,
            path: '/',
            sameSite: 'lax',
          });
        }
      } catch (error) {
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        logger.error('Error al establecer auth_token cookie:', normalizedError, { module: 'auth', action: 'setAuthTokenCookie' });
      }
    }
  },
  removeItem: (name: string): void => {
    Cookies.remove(name, { path: '/' });
    // También eliminar la cookie auth_token cuando se elimina auth-storage
    if (name === 'auth-storage') {
      Cookies.remove('auth_token', { path: '/' });
      Cookies.remove('auth-storage', { path: '/' });
    }
  },
};

// IpService devuelve alpha-2 (estándar de geolocalización IP). El backend
// (ms-users-auth) exige alpha-3 desde Phase 6, así que convertimos antes
// de exponer el código al resto del store. `toIso3` ya cae a
// DEFAULT_SIGNER_COUNTRY_ISO3 ('CHL') si el input es null/undefined.
const detectCountryCode = async (): Promise<string> => {
  try {
    const code = await IpService.getInstance().detectCountryCode();
    return toIso3(code);
  } catch {
    return DEFAULT_SIGNER_COUNTRY_ISO3;
  }
};

// `GET /auth/verify` puede devolver un `user` degradado (parcial) cuando el
// fallback del gateway entra: en ese caso campos como `isActive`, `firstName`,
// `phone` o `dni` llegan `undefined`. Reemplazar el user completo borraría esos
// datos ya conocidos de la sesión (p. ej. el badge "Estado de cuenta" mostraría
// "Inactivo"). Este merge conserva el valor previo para cada clave que venga
// `undefined` en la respuesta nueva. Es seguro porque ambos caminos
// (`loginWithToken`/`checkAuthStatus`) verifican el token de la MISMA sesión.
const mergeDefinedUser = (prev: IUser | null, next: IUser): IUser => {
  if (!prev) return next;
  const merged = { ...prev } as Record<string, unknown>;
  Object.entries(next).forEach(([key, value]) => {
    if (value !== undefined) {
      merged[key] = value;
    }
  });
  return merged as unknown as IUser;
};

const createAuthStore: StateCreator<AuthStore, [['zustand/devtools', never], ['zustand/persist', unknown]], []> = (set, get) => ({
  status: 'checking',
  token: null,
  user: null,
  errorMessage: null,
  successMessage: null,
  pending2faEmail: null,

  login: async (email: string, password: string) => {
    try {
      set({ status: 'checking' });

      const loginData: IUserLogin = { email, password };
      const response = await authRepository.login(loginData);

      // Respuesta discriminada 2FA: backend no emite JWT hasta validar el codigo.
      if (response.requires2fa) {
        set(
          {
            status: 'requires-2fa',
            token: null,
            user: null,
            errorMessage: null,
            pending2faEmail: email,
          },
          false,
          'login-requires-2fa'
        );
        return;
      }

      set(
        {
          status: 'authenticated',
          token: response.accessToken,
          user: response.user,
          errorMessage: null,
          pending2faEmail: null,
        },
        false,
        'login'
      );
    } catch (error) {
      if (error instanceof Error) {
        set(
          { status: 'not-authenticated', token: null, user: null, errorMessage: error.message, pending2faEmail: null },
          false,
          'login-error'
        );
      }
      throw error;
    }
  },

  verify2faLogin: async (code: string) => {
    const email = get().pending2faEmail;
    if (!email) {
      throw new Error('No hay un login 2FA pendiente');
    }
    try {
      set({ status: 'checking' });
      const response = await authRepository.loginWith2fa({ email, code });
      set(
        {
          status: 'authenticated',
          token: response.accessToken,
          user: response.user,
          errorMessage: null,
          pending2faEmail: null,
        },
        false,
        'verify-2fa-success'
      );
    } catch (error) {
      if (error instanceof Error) {
        // Mantener pending2faEmail para que el usuario pueda reintentar
        set({ status: 'requires-2fa', errorMessage: error.message }, false, 'verify-2fa-error');
      }
      throw error;
    }
  },

  sendMagicLink: async (email: string) => {
    try {
      const response = await authRepository.sendMagicLink({ email });
      set({ successMessage: response.message ?? null, errorMessage: null }, false, 'send-magic-link');
      return response;
    } catch (error) {
      if (error instanceof Error) {
        set({ errorMessage: error.message, successMessage: null }, false, 'send-magic-link-error');
      }
      throw error;
    }
  },

  cancel2fa: () => {
    set({ status: 'not-authenticated', pending2faEmail: null, errorMessage: null }, false, 'cancel-2fa');
  },

  loginWithGoogle: async () => {
    try {
      set({ status: 'checking' });

      const googleCredential = await firebaseAuthDataSource.signInWithGoogle();

      const response = await authRepository.socialLogin({
        email: googleCredential.user.email,
        network: ESignInMethod.GOOGLE,
        socialToken: googleCredential.idToken,
      });

      set(
        {
          status: 'authenticated',
          token: response.accessToken,
          user: response.user,
          errorMessage: null,
        },
        false,
        'login-with-google'
      );
    } catch (error) {
      // Solo auto-registramos cuando el backend responde con el código exacto USER_NOT_FOUND.
      // Un match por substring ("not found") podría dispararse con errores de red/proxy y crear
      // cuentas no deseadas aceptando ToS silenciosamente.
      if (error instanceof Error && /^(AUTH\.)?USER_NOT_FOUND$/.test(error.message.trim())) {
        try {
          logger.info('Auto-registering Google user after USER_NOT_FOUND', {
            module: 'auth-store',
            action: 'socialRegister',
            network: ESignInMethod.GOOGLE,
          });
          const credential = await firebaseAuthDataSource.signInWithGoogle();
          const [firstName, ...lastNameParts] = (credential.user.displayName || '').split(' ');
          const countryCode = await detectCountryCode();

          const response = await authRepository.socialRegister({
            email: credential.user.email,
            first_name: firstName || '',
            last_name: lastNameParts.join(' ') || '',
            social_token: credential.idToken,
            network: ESignInMethod.GOOGLE,
            terms_accepted: true,
            country_code: countryCode,
          });

          set(
            {
              status: 'authenticated',
              token: response.accessToken,
              user: response.user,
              errorMessage: null,
            },
            false,
            'register-with-google'
          );
          return;
        } catch (registerError) {
          if (registerError instanceof Error) {
            set(
              { status: 'not-authenticated', token: null, user: null, errorMessage: registerError.message },
              false,
              'register-with-google-error'
            );
          }
          throw registerError;
        }
      }

      if (error instanceof Error) {
        set({ status: 'not-authenticated', token: null, user: null, errorMessage: error.message }, false, 'login-with-google-error');
      }
      throw error;
    }
  },

  loginWithMicrosoft: async () => {
    try {
      set({ status: 'checking' });
      // Redirect flow: la pestaña navega a Microsoft. La promesa NO resuelve
      // en este contexto — la lógica continúa en `/auth/redirect` vía
      // `completeMicrosoftLogin` después del callback.
      await msalAuthDataSource.initiateMicrosoftSignIn();
    } catch (error) {
      if (error instanceof Error) {
        set(
          {
            status: 'not-authenticated',
            token: null,
            user: null,
            errorMessage: error.message,
          },
          false,
          'login-with-microsoft-error'
        );
      }
      throw error;
    }
  },

  completeMicrosoftLogin: async (credential) => {
    try {
      set({ status: 'checking' });

      const tenantId = credential.tenantId;

      try {
        const response = await authRepository.socialLogin({
          email: credential.user.email,
          network: ESignInMethod.MICROSOFT,
          socialToken: credential.idToken,
        });

        set(
          {
            status: 'authenticated',
            token: response.accessToken,
            user: response.user,
            errorMessage: null,
          },
          false,
          'login-with-microsoft'
        );
      } catch (loginError) {
        // Fallback: solo auto-registramos cuando el backend devuelve el código exacto USER_NOT_FOUND.
        // Match estricto para evitar registro silencioso ante errores genéricos de red/proxy.
        if (loginError instanceof Error && /^(AUTH\.)?USER_NOT_FOUND$/.test(loginError.message.trim())) {
          logger.info('Auto-registering Microsoft user after USER_NOT_FOUND', {
            module: 'auth-store',
            action: 'socialRegister',
            network: ESignInMethod.MICROSOFT,
          });
          const [firstName, ...lastNameParts] = (credential.user.displayName || '').split(' ');
          const countryCode = await detectCountryCode();

          const response = await authRepository.socialRegister({
            email: credential.user.email,
            first_name: firstName || '',
            last_name: lastNameParts.join(' ') || '',
            social_token: credential.idToken,
            network: ESignInMethod.MICROSOFT,
            terms_accepted: true,
            country_code: countryCode,
            ...(tenantId ? { tenant_id: tenantId } : {}),
          });

          set(
            {
              status: 'authenticated',
              token: response.accessToken,
              user: response.user,
              errorMessage: null,
            },
            false,
            'register-with-microsoft'
          );
          return;
        }
        throw loginError;
      }
    } catch (error) {
      if (error instanceof Error) {
        set(
          {
            status: 'not-authenticated',
            token: null,
            user: null,
            errorMessage: error.message,
          },
          false,
          'complete-microsoft-login-error'
        );
      }
      throw error;
    }
  },

  loginWithToken: async (token: string) => {
    try {
      set({ status: 'checking' });

      const response = await authRepository.loginWithToken(token);
      logger.debug('Login with token response', { module: 'auth-store', userId: response.user?.id });

      set(
        {
          status: 'authenticated',
          token: response.accessToken,
          // Merge defensivo: un verify degradado no debe borrar datos buenos.
          user: mergeDefinedUser(get().user, response.user),
          errorMessage: null,
        },
        false,
        'login-with-token'
      );
    } catch (error) {
      if (error instanceof Error) {
        set({ status: 'not-authenticated', token: null, user: null, errorMessage: error.message }, false, 'login-with-token-error');
      }
      throw error;
    }
  },

  impersonateUser: async (email: string) => {
    try {
      set({ status: 'checking' });

      // Hacer la petición usando el repositorio de auth (misma lógica que login)
      const response = await authRepository.impersonateUser(email);
      logger.info('User impersonation successful', { module: 'auth-store', email, userId: response.user?.id });

      set(
        {
          status: 'authenticated',
          token: response.accessToken,
          user: response.user,
          errorMessage: null,
        },
        false,
        'impersonate-user'
      );
      window.location.href = '/dashboard';
    } catch (error) {
      if (error instanceof Error) {
        set({ status: 'not-authenticated', token: null, user: null, errorMessage: error.message }, false, 'impersonate-user-error');
      }
      throw error;
    }
  },

  register: async (data: IUserRegister) => {
    try {
      set({ status: 'checking' });

      const registerData: IUserRegister = {
        name: data.name,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        termsAccepted: data.termsAccepted,
        ...(data.termsUrl ? { termsUrl: data.termsUrl } : {}),
        ...(data.countryCode ? { countryCode: data.countryCode } : {}),
      };

      await authRepository.register(registerData);

      set(
        {
          status: 'not-authenticated',
          token: null,
          user: null,
          errorMessage: null,
        },
        false,
        'register'
      );
    } catch (error) {
      if (error instanceof Error) {
        set({ status: 'not-authenticated', token: null, user: null, errorMessage: error.message }, false, 'register-error');
      }
      throw error;
    }
  },

  forgotPassword: async (email: string) => {
    try {
      set({ status: 'checking' });

      const forgotPasswordData: IForgotPassword = { email };
      const response = await authRepository.forgotPassword(forgotPasswordData);

      set(
        {
          status: 'not-authenticated',
          token: null,
          user: null,
          errorMessage: null,
          successMessage: response.message,
        },
        false,
        'forgot-password'
      );

      return response;
    } catch (error) {
      if (error instanceof Error) {
        set(
          { status: 'not-authenticated', token: null, user: null, errorMessage: error.message, successMessage: null },
          false,
          'forgot-password-error'
        );
      }
      throw error;
    }
  },

  resetPassword: async (data: IChangePassword) => {
    try {
      set({ status: 'checking' });

      const changePasswordData: IChangePassword = {
        email: data.email,
        code: data.code,
        password: data.password,
      };

      const response = await authRepository.resetPassword(changePasswordData);

      set(
        {
          status: 'not-authenticated',
          token: null,
          user: null,
          errorMessage: null,
          successMessage: response.message,
        },
        false,
        'change-password'
      );

      return response;
    } catch (error) {
      if (error instanceof Error) {
        set(
          { status: 'not-authenticated', token: null, user: null, errorMessage: error.message, successMessage: null },
          false,
          'change-password-error'
        );
      }
      throw error;
    }
  },

  resendActivationCode: async (email: string) => {
    try {
      const response = await authRepository.resendActivationCode(email);
      return response;
    } catch (error) {
      throw error;
    }
  },

  validateAccount: async (data: IValidateAccount) => {
    try {
      const response = await authRepository.validateAccount(data);
      return response;
    } catch (error) {
      throw error;
    }
  },

  logout: () => {
    // Cerrar sesión en Firebase si está disponible
    firebaseAuthDataSource.signOut().catch((error) => {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error al cerrar sesión en Firebase:', normalizedError, { module: 'auth', action: 'firebaseSignOut' });
    });
    set({ status: 'not-authenticated', token: null, user: null, errorMessage: null, pending2faEmail: null }, false, 'logout');
    // Eliminar TODO el estado del navegador: cookies, localStorage y
    // sessionStorage. Se llama después del `set` para que el estado
    // persistido (auth-storage / auth_token) también quede borrado.
    clearBrowserStorage();
  },

  checkAuthStatus: async () => {
    try {
      set({ status: 'checking' });
      // Verificar si hay un token en la cookie
      const token = Cookies.get('auth_token');

      if (!token) {
        set({ status: 'not-authenticated', token: null, user: null, pending2faEmail: null }, false, 'check-auth-no-token');
        return;
      }

      try {
        const response = await authRepository.loginWithToken(token);

        set(
          {
            status: 'authenticated',
            token: response.accessToken,
            // Merge defensivo: un verify degradado no debe borrar datos buenos.
            user: mergeDefinedUser(get().user, response.user),
            errorMessage: null,
            pending2faEmail: null,
          },
          false,
          'check-auth-success'
        );
      } catch (error) {
        // Si el backend rechaza el token, la sesión local no es fuente de verdad.
        const normalizedError = error instanceof Error ? error : new Error(String(error));
        logger.error('Error al verificar token persistido:', normalizedError, { module: 'auth', action: 'verifyPersistedToken' });
        Cookies.remove('auth_token', { path: '/' });
        Cookies.remove('auth-storage', { path: '/' });
        set(
          { status: 'not-authenticated', token: null, user: null, errorMessage: null, pending2faEmail: null },
          false,
          'check-auth-invalid-token'
        );
      }
    } catch (error) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error en checkAuthStatus:', normalizedError, { module: 'auth', action: 'checkAuthStatus' });
      set({ status: 'not-authenticated', token: null, user: null, errorMessage: null, pending2faEmail: null }, false, 'check-auth-error');
    }
  },

  getCurrentUser: () => {
    const state = useAuthStore.getState();
    return state.user;
  },

  updateUser: (partialUser: Partial<IUser>) => {
    const currentUser = useAuthStore.getState().user;
    if (currentUser) {
      set(
        {
          user: { ...currentUser, ...partialUser },
        },
        false,
        'update-user'
      );
    }
  },
});

// Crear la store con middleware de persistencia y devtools
export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(createAuthStore, {
      name: 'auth-storage',
      storage: createJSONStorage(() => cookieStorage),
      partialize: (state) => ({
        token: state.token,
        user: state.user,
      }),
      onRehydrateStorage: () => (state) => {
        // Log cuando se rehidrata el estado desde storage
        if (state) {
          logger.debug('Auth store rehydrated', {
            module: 'auth-store',
            hasUser: !!state.user,
            hasToken: !!state.token,
            status: state.status,
          });
        }
      },
    }),
    {
      name: 'AuthStore', // Nombre visible en Redux DevTools
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);
